import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, TrendingUp } from 'lucide-react';
import chartService from '../services/chartService';
import SVGChart from './SVGChart';
import TechnicalAnalysisPanel from './TechnicalAnalysisPanel';
import SwapTable from './SwapTable';
import { useAuth } from '../contexts/AuthContext';
import websocketService from '../services/websocketService';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ [ErrorBoundary] SVGChart Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-red-800 rounded-lg border-4 border-red-400">
          <div className="text-center">
            <div className="text-white font-bold text-lg mb-2">❌ SVGChart Error</div>
            <div className="text-red-200 text-sm">Error: {this.state.error?.message}</div>
            <button 
              onClick={() => this.setState({ hasError: false, error: null })}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const PriceChartModal = ({ token, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(0);
  const [showTechnicalAnalysis, setShowTechnicalAnalysis] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [timeframe, setTimeframe] = useState('5MIN');
  const [volume, setVolume] = useState(0);
  const [liveMarketCap, setLiveMarketCap] = useState(null);
  const [liveLiquidity, setLiveLiquidity] = useState(null);
  const [livePriceChange, setLivePriceChange] = useState(null);
  const [tokenAnalytics, setTokenAnalytics] = useState(null);
  const [realTimeData, setRealTimeData] = useState(null);
  
  // ✅ CRITICAL FIX: Fetch historical swaps from API on mount
  useEffect(() => {
    const loadHistoricalSwaps = async () => {
      if (!token?.contractAddress && !token?.mint && !token?.address) {
        console.warn('⚠️ [PriceChartModal] No token address available for fetching swaps');
        return;
      }

      const tokenAddress = token.contractAddress || token.mint || token.address;
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.degen-oracle.com';
      
      try {
        console.log(`📊 [PriceChartModal] Fetching historical swaps for ${tokenAddress.substring(0, 8)}...`);
        
        // Try /api/charts/swaps/:token endpoint first
        const swapsResponse = await fetch(`${API_BASE}/api/charts/swaps/${tokenAddress}?limit=100`);
        let historicalSwaps = [];
        
        if (swapsResponse.ok) {
          const swapsData = await swapsResponse.json();
          if (swapsData.success && swapsData.swaps && Array.isArray(swapsData.swaps)) {
            historicalSwaps = swapsData.swaps;
            console.log(`✅ [PriceChartModal] Loaded ${historicalSwaps.length} historical swaps from API`);
          }
        } else {
          console.warn(`⚠️ [PriceChartModal] Swaps API returned ${swapsResponse.status}, trying realtime-data endpoint...`);
          
          // Fallback to /api/tokens/:address/realtime-data
          const realtimeResponse = await fetch(`${API_BASE}/api/tokens/${tokenAddress}/realtime-data`);
          if (realtimeResponse.ok) {
            const realtimeData = await realtimeResponse.json();
            if (realtimeData.success && realtimeData.data) {
              historicalSwaps = realtimeData.data.swapHistory || realtimeData.data.recentSwaps || [];
              console.log(`✅ [PriceChartModal] Loaded ${historicalSwaps.length} swaps from realtime-data endpoint`);
            }
          }
        }
        
        // Merge with token.recentSwaps if available
        const tokenSwaps = token?.recentSwaps || [];
        const allSwaps = [...historicalSwaps, ...tokenSwaps];
        
        // Remove duplicates by signature
        const uniqueSwaps = Array.from(
          new Map(allSwaps.map(swap => [swap.signature, swap])).values()
        );
        
        // Sort by timestamp (newest first)
        uniqueSwaps.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        
        console.log(`📊 [PriceChartModal] Total unique swaps: ${uniqueSwaps.length} (${historicalSwaps.length} historical + ${tokenSwaps.length} from token prop)`);
        
        setRealTimeData({
          recentSwaps: uniqueSwaps,
          swapHistory: uniqueSwaps
        });
      } catch (error) {
        console.error('❌ [PriceChartModal] Error loading historical swaps:', error);
        // Still set token.recentSwaps if available
        if (token?.recentSwaps && token.recentSwaps.length > 0) {
          setRealTimeData({
            recentSwaps: token.recentSwaps,
            swapHistory: token.recentSwaps
          });
        }
      }
    };

    loadHistoricalSwaps();
  }, [token?.contractAddress, token?.mint, token?.address]);
  
  // Use ref to track previous price for accurate change calculation
  const previousPriceRef = useRef(null);
  
  const isPremiumUser = isAuthenticated && user?.isPremium;

  const handlePriceUpdate = (priceData) => {
    console.log(`📡 [PRICE-MODAL] 🚀 Real-time price update received:`, priceData);
    
    // Extract data from DexScreenerStyleMonitor broadcastMetrics structure
    // Structure: { tokenAddress, priceUsd, currentPrice, price, marketCap, liquidity, volume24h, volume6h, volume1h, volume5m, ... }
    const data = priceData.data || priceData;
    
    // Extract price (priority: priceUsd > currentPrice > price)
    const price = data.priceUsd || data.currentPrice || data.price || priceData.price || priceData.priceUsd;
    
    if (!price) {
      console.warn(`📡 [PRICE-MODAL] ⚠️ No price found in data:`, {
        priceData,
        data,
        hasPriceUsd: !!data.priceUsd,
        hasCurrentPrice: !!data.currentPrice,
        hasPrice: !!data.price
      });
      return;
    }
    
    // 🚨 CRITICAL FIX: Extract live market cap and liquidity from DexScreenerStyleMonitor
    // Check both data.marketCap and priceData.marketCap (structure might vary)
    const marketCap = data.marketCap !== undefined && data.marketCap !== null 
      ? data.marketCap 
      : (priceData.marketCap !== undefined && priceData.marketCap !== null ? priceData.marketCap : null);
    
    if (marketCap !== null && marketCap !== undefined && marketCap > 0) {
      setLiveMarketCap(marketCap);
      console.log(`📡 [PRICE-MODAL] 💰 Live market cap: $${(marketCap / 1e6).toFixed(2)}M`);
    } else {
      console.warn(`📡 [PRICE-MODAL] ⚠️ Market cap not found or is 0:`, {
        dataMarketCap: data.marketCap,
        priceDataMarketCap: priceData.marketCap,
        dataKeys: Object.keys(data)
      });
    }
    
    const liquidity = data.liquidity !== undefined && data.liquidity !== null
      ? data.liquidity
      : (priceData.liquidity !== undefined && priceData.liquidity !== null ? priceData.liquidity : null);
    
    if (liquidity !== null && liquidity !== undefined && liquidity > 0) {
      setLiveLiquidity(liquidity);
      console.log(`📡 [PRICE-MODAL] 💧 Live liquidity: $${(liquidity / 1e6).toFixed(2)}M`);
    }
    
    // Update volume based on current timeframe
    updateVolumeFromLiveData(data);
    
    // Update price change from live data based on timeframe (from DexScreenerStyleMonitor)
    updatePriceChangeFromLiveData(data);
    
    // Update the current price and ref (for fallback calculation if needed)
    setCurrentPrice(price);
    previousPriceRef.current = price;
    
    console.log(`📡 [PRICE-MODAL] ✅ Updated price for ${token.symbol}: ${price}, marketCap: ${marketCap ? `$${(marketCap / 1e6).toFixed(2)}M` : 'N/A'}`);
  };
  
  // Store latest live data for volume updates when timeframe changes
  const latestLiveDataRef = useRef(null);
  
  // Update volume from live data based on timeframe
  const updateVolumeFromLiveData = (liveData) => {
    if (!liveData) return;
    
    // Store latest live data for timeframe changes
    latestLiveDataRef.current = liveData;
    
    let volumeValue = 0;
    switch (timeframe) {
      case '1MIN':
      case '5MIN':
        volumeValue = liveData.volume5m || 0;
        break;
      case '10MIN': // Changed from 15MIN
      case '1H':
        volumeValue = liveData.volume1h || 0;
        break;
      case '4H':
      case '6H':
        volumeValue = liveData.volume6h || 0;
        break;
      case '1D':
      default:
        volumeValue = liveData.volume24h || 0;
        break;
    }
    
    if (volumeValue > 0) {
      setVolume(volumeValue);
      console.log(`📡 [PRICE-MODAL] 📊 Live volume (${timeframe}): $${(volumeValue / 1e6).toFixed(2)}M`);
    }
  };
  
  // Update price change from live data based on timeframe (from DexScreenerStyleMonitor)
  const updatePriceChangeFromLiveData = (liveData) => {
    if (!liveData) return;
    
    let priceChangeValue = 0;
    switch (timeframe) {
      case '1MIN':
      case '5MIN':
        priceChangeValue = liveData.priceChange5m || 0;
        break;
      case '10MIN': // Changed from 15MIN
      case '1H':
        priceChangeValue = liveData.priceChange1h || 0;
        break;
      case '4H':
      case '6H':
        priceChangeValue = liveData.priceChange6h || 0;
        break;
      case '1D':
      default:
        priceChangeValue = liveData.priceChange24h || 0;
        break;
    }
    
    // Use live price change from DexScreenerStyleMonitor (more accurate)
    if (priceChangeValue !== undefined && priceChangeValue !== null) {
      setLivePriceChange(priceChangeValue);
      setPriceChange(priceChangeValue);
      console.log(`📡 [PRICE-MODAL] 📊 Live price change (${timeframe}): ${priceChangeValue.toFixed(2)}%`);
    } else if (previousPriceRef.current !== null && previousPriceRef.current !== (liveData.priceUsd || liveData.currentPrice || liveData.price)) {
      // Fallback: Calculate locally if live data not available
      const price = liveData.priceUsd || liveData.currentPrice || liveData.price;
      if (price && previousPriceRef.current) {
        const change = ((price - previousPriceRef.current) / previousPriceRef.current) * 100;
        setPriceChange(change);
        console.log(`📡 [PRICE-MODAL] 📊 Calculated price change: ${change.toFixed(2)}% (fallback)`);
      }
    }
  };

  // Subscribe to WebSocket on mount, unsubscribe on unmount
  const subscribedAddressRef = useRef(null);
  
  useEffect(() => {
    const contractAddress = token?.contractAddress;
    if (!contractAddress) return;
    
    // Only subscribe if we haven't subscribed to this address yet
    if (subscribedAddressRef.current !== contractAddress) {
      // Unsubscribe from previous address if exists
      if (subscribedAddressRef.current) {
        websocketService.unsubscribeFromToken(subscribedAddressRef.current);
      }
      
      // Subscribe to new address
      websocketService.subscribeToToken(contractAddress);
      subscribedAddressRef.current = contractAddress;
    }
    
    // Cleanup only on unmount
    return () => {
      if (subscribedAddressRef.current) {
        websocketService.unsubscribeFromToken(subscribedAddressRef.current);
        subscribedAddressRef.current = null;
      }
    };
  }, [token?.contractAddress]); // Depend on contract address to handle token changes

  // Load data when token changes
  useEffect(() => {
    if (token?.contractAddress) {
      // Load initial price from Jupiter API (more reliable than our cached data)
      loadCurrentPriceFromJupiter();
      loadTokenAnalytics();
      loadRealTimeData();
    }
  }, [token?.contractAddress]);

  useEffect(() => {
    if (tokenAnalytics && timeframe) {
      updateVolumeForTimeframe();
    }
  }, [timeframe, tokenAnalytics]);
  
  // Update volume and price change when timeframe changes (if we have live data)
  useEffect(() => {
    // If we have live data, use it for volume and price change calculation
    if (latestLiveDataRef.current) {
      updateVolumeFromLiveData(latestLiveDataRef.current);
      updatePriceChangeFromLiveData(latestLiveDataRef.current);
    } else if (tokenAnalytics) {
      // Fallback to static analytics if no live data yet
      updateVolumeForTimeframe();
    }
  }, [timeframe]);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    const handleFullStateUpdate = (data) => {
      if (!data.tokens || !token?.contractAddress) return;
      
      // 🚨 CRITICAL FIX: Case-insensitive address matching
      const tokenContractAddress = (token?.contractAddress || token?.mint || token?.address || '').toLowerCase();
      
      // Find this token in the full state update
      const updatedToken = data.tokens.find(t => {
        const tAddress = (t.contractAddress || t.tokenAddress || t.mint || '').toLowerCase();
        return tAddress === tokenContractAddress;
      });
      
      if (updatedToken) {
        console.log(`🔄 [PriceChartModal] Full state update for ${updatedToken.symbol || 'token'}`);
        
        // 🚨 CRITICAL FIX: Update price and market cap from full state update
        if (updatedToken.priceUsd || updatedToken.currentPrice || updatedToken.price) {
          const price = updatedToken.priceUsd || updatedToken.currentPrice || updatedToken.price;
          setCurrentPrice(price);
          previousPriceRef.current = price;
          console.log(`📡 [PriceChartModal] Updated price from fullStateUpdate: ${price}`);
        }
        
        if (updatedToken.marketCap !== undefined && updatedToken.marketCap !== null && updatedToken.marketCap > 0) {
          setLiveMarketCap(updatedToken.marketCap);
          console.log(`📡 [PriceChartModal] Updated market cap from fullStateUpdate: $${(updatedToken.marketCap / 1e6).toFixed(2)}M`);
        }
        
        if (updatedToken.liquidity !== undefined && updatedToken.liquidity !== null && updatedToken.liquidity > 0) {
          setLiveLiquidity(updatedToken.liquidity);
          console.log(`📡 [PriceChartModal] Updated liquidity from fullStateUpdate: $${(updatedToken.liquidity / 1e6).toFixed(2)}M`);
        }
        
        // Update volume and price change if available
        if (updatedToken.volume24h || updatedToken.priceChange24h !== undefined) {
          updateVolumeFromLiveData(updatedToken);
          updatePriceChangeFromLiveData(updatedToken);
        }
        
        // Update swap history if available
        if (updatedToken.recentSwaps && updatedToken.recentSwaps.length > 0) {
          console.log(`🔄 [PriceChartModal] Full state update: ${updatedToken.recentSwaps.length} swaps for ${updatedToken.symbol}`);
          
          // ✅ CRITICAL FIX: Merge with existing swaps, don't replace
          setRealTimeData(prevData => {
            if (!prevData || !prevData.swapHistory || prevData.swapHistory.length === 0) {
              // No existing data, use the broadcast data
              return {
                recentSwaps: updatedToken.recentSwaps,
                swapHistory: updatedToken.recentSwaps
              };
            }
            
            // Merge: Keep existing historical swaps, prepend any new swaps from broadcast
            const existingSignatures = new Set(prevData.swapHistory.map(s => s.signature));
            const newSwaps = updatedToken.recentSwaps.filter(s => !existingSignatures.has(s.signature));
            
            if (newSwaps.length > 0) {
              console.log(`✅ [PriceChartModal] Merged ${newSwaps.length} new swaps with ${prevData.swapHistory.length} existing swaps`);
              return {
                ...prevData,
                swapHistory: [...newSwaps, ...prevData.swapHistory],
                recentSwaps: [...newSwaps, ...prevData.swapHistory].slice(0, 100) // Keep last 100
              };
            }
            
            // No new swaps, keep existing data
            return prevData;
          });
        }
      } else {
        console.log(`⚠️ [PriceChartModal] Token not found in fullStateUpdate (looking for ${tokenContractAddress.substring(0, 8)}...)`);
      }
    };

    const handleSwapUpdate = (data) => {
      const tokenAddress = token?.contractAddress || token?.mint || token?.address;
      if (data.tokenAddress === tokenAddress || data.data?.tokenAddress === tokenAddress) {
        const swapData = data.swapData || data.data || data;
        console.log('🔄 [PriceChartModal] Real-time swap update received:', swapData);
        
        // ✅ CRITICAL FIX: Append new swap to existing data instead of reloading
        setRealTimeData(prevData => {
          // Initialize if no data exists yet
          if (!prevData) {
            return {
              recentSwaps: [swapData],
              swapHistory: [swapData],
              totalSwaps: 1
            };
          }
          
          // Check if swap already exists (by signature)
          const existingSignatures = new Set((prevData.swapHistory || []).map(s => s.signature));
          if (existingSignatures.has(swapData.signature)) {
            console.log(`⚠️ [PriceChartModal] Swap ${swapData.signature?.slice(0, 8)}... already exists, skipping`);
            return prevData;
          }
          
          const updatedSwapHistory = [swapData, ...(prevData.swapHistory || [])];
          const updatedRecentSwaps = [swapData, ...(prevData.recentSwaps || [])];
          
          // Sort by timestamp (newest first)
          updatedSwapHistory.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          updatedRecentSwaps.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          
          console.log(`✅ [PriceChartModal] Added live swap to table (now ${updatedSwapHistory.length} total swaps)`);
          
          return {
            ...prevData,
            swapHistory: updatedSwapHistory,
            recentSwaps: updatedRecentSwaps,
            totalSwaps: updatedSwapHistory.length
          };
        });
      }
    };

    const handleWebSocketPriceUpdate = (data) => {
      // 🚨 CRITICAL FIX: Case-insensitive address matching
      const updateTokenAddress = (data.tokenAddress || data.data?.tokenAddress || '').toLowerCase();
      const tokenContractAddress = (token?.contractAddress || token?.mint || token?.address || '').toLowerCase();
      
      if (updateTokenAddress && tokenContractAddress && updateTokenAddress === tokenContractAddress) {
        console.log('📈 [PriceChartModal] Real-time price update received:', {
          tokenAddress: data.tokenAddress,
          hasPriceData: !!data.priceData,
          hasData: !!data.data,
          priceDataKeys: data.priceData ? Object.keys(data.priceData) : [],
          dataKeys: data.data ? Object.keys(data.data) : []
        });
        
        // Extract priceData - WebSocket service sends: { tokenAddress, priceData, timestamp }
        // priceData contains: { priceUsd, marketCap, liquidity, volume24h, ... }
        const priceData = data.priceData || data.data || data;
        
        // Ensure tokenAddress is included in priceData for handlePriceUpdate
        if (!priceData.tokenAddress) {
          priceData.tokenAddress = data.tokenAddress;
        }
        
        handlePriceUpdate(priceData);
      } else {
        // Debug: Log when address doesn't match
        if (updateTokenAddress && tokenContractAddress) {
          console.log(`⚠️ [PriceChartModal] Address mismatch - Update: ${updateTokenAddress.substring(0, 8)}... vs Token: ${tokenContractAddress.substring(0, 8)}...`);
        }
      }
    };

    // Add event listeners
    websocketService.on('fullStateUpdate', handleFullStateUpdate);
    websocketService.on('swapUpdate', handleSwapUpdate);
    websocketService.on('priceUpdate', handleWebSocketPriceUpdate);

    // Cleanup
    return () => {
      websocketService.off('fullStateUpdate', handleFullStateUpdate);
      websocketService.off('swapUpdate', handleSwapUpdate);
      websocketService.off('priceUpdate', handleWebSocketPriceUpdate);
    };
  }, [token?.contractAddress]);

  const loadCurrentPriceFromJupiter = async () => {
    try {
      console.log(`📡 [PRICE-MODAL] Loading initial price from Jupiter API...`);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.degen-oracle.com';
      const response = await fetch(`${API_BASE}/api/jupiter/raw/${token.contractAddress}`);
      const data = await response.json();
      
      if (data.success && data.data?.[0]?.usdPrice) {
        const jupiterPrice = data.data[0].usdPrice;
        setCurrentPrice(jupiterPrice);
        previousPriceRef.current = jupiterPrice; // Initialize the ref with Jupiter price
        console.log(`📡 [PRICE-MODAL] ✅ Initial price loaded from Jupiter: ${jupiterPrice}`);
      } else {
        console.log(`📡 [PRICE-MODAL] ⚠️ Failed to get Jupiter price, falling back to chart service`);
        loadCurrentPrice(); // Fallback to original method
      }
    } catch (error) {
      console.error('Failed to load current price from Jupiter:', error);
      loadCurrentPrice(); // Fallback to original method
    }
  };

  const loadCurrentPrice = async () => {
    try {
      const response = await chartService.getCurrentPrice(token.contractAddress);
      if (response.success) {
        setCurrentPrice(response.price);
        previousPriceRef.current = response.price; // Initialize the ref with initial price
        console.log(`📡 [PRICE-MODAL] 📊 Initial price loaded: ${response.price}`);
      }
    } catch (error) {
      console.error('Failed to load current price:', error);
    }
  };

  const loadTokenAnalytics = async () => {
    try {
      const response = await chartService.getTokenAnalytics(token.contractAddress);
      if (response.success) {
        setTokenAnalytics(response.data);
      }
    } catch (error) {
      console.error('Failed to load token analytics:', error);
    }
  };

  const loadRealTimeData = async (retryCount = 0) => {
    try {
      console.log(`🔍 [PriceChartModal] Loading real-time data for ${token.contractAddress} (attempt ${retryCount + 1})`);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.degen-oracle.com';
      const response = await fetch(`${API_BASE}/api/tokens/${token.contractAddress}/realtime-data`);
      console.log(`📡 [PriceChartModal] Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 [PriceChartModal] Loaded real-time data:', data.data);
        setRealTimeData(data.data);
      } else {
        console.error('❌ [PriceChartModal] Failed to load real-time data:', response.status, response.statusText);
        // Try to get error details
        const errorText = await response.text();
        console.error('❌ [PriceChartModal] Error details:', errorText);
        
        // Retry up to 3 times for 404 errors (deployment might be in progress)
        if (response.status === 404 && retryCount < 3) {
          console.log(`🔄 [PriceChartModal] Retrying in 2 seconds... (${retryCount + 1}/3)`);
          setTimeout(() => loadRealTimeData(retryCount + 1), 2000);
        }
      }
    } catch (error) {
      console.error('❌ [PriceChartModal] Error loading real-time data:', error);
      
      // Retry on network errors
      if (retryCount < 3) {
        console.log(`🔄 [PriceChartModal] Retrying in 2 seconds... (${retryCount + 1}/3)`);
        setTimeout(() => loadRealTimeData(retryCount + 1), 2000);
      }
    }
  };

  const updateVolumeForTimeframe = () => {
    if (!tokenAnalytics) return;




    let volumeValue = 0;
    switch (timeframe) {
      case '1MIN':
      case '5MIN':
        // For 5m, use 5m data or fallback to 1h if 5m is empty
        const buyVol5m = parseFloat(tokenAnalytics.totalBuyVolume?.['5m']) || 0;
        const sellVol5m = parseFloat(tokenAnalytics.totalSellVolume?.['5m']) || 0;
        volumeValue = buyVol5m + sellVol5m;
        // If 5m is empty, use 1h data
        if (volumeValue === 0) {
          const buyVol1h = parseFloat(tokenAnalytics.totalBuyVolume?.['1h']) || 0;
          const sellVol1h = parseFloat(tokenAnalytics.totalSellVolume?.['1h']) || 0;
          volumeValue = buyVol1h + sellVol1h;
        }
        break;
      case '10MIN': // Changed from 15MIN
      case '1H':
        const buyVol1h = parseFloat(tokenAnalytics.totalBuyVolume?.['1h']) || 0;
        const sellVol1h = parseFloat(tokenAnalytics.totalSellVolume?.['1h']) || 0;
        volumeValue = buyVol1h + sellVol1h;
        break;
      case '4H':
      case '6H':
        const buyVol6h = parseFloat(tokenAnalytics.totalBuyVolume?.['6h']) || 0;
        const sellVol6h = parseFloat(tokenAnalytics.totalSellVolume?.['6h']) || 0;
        volumeValue = buyVol6h + sellVol6h;
        break;
      case '1D':
      default:
        const buyVol24h = parseFloat(tokenAnalytics.totalBuyVolume?.['24h']) || 0;
        const sellVol24h = parseFloat(tokenAnalytics.totalSellVolume?.['24h']) || 0;
        volumeValue = buyVol24h + sellVol24h;
        break;
    }
    

    setVolume(volumeValue);
  };


  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    if (price < 0.0001) return `$${price.toExponential(2)}`;
    if (price < 1) return `$${price.toFixed(6)}`; // 6 decimals for prices < 1
    if (price < 100) return `$${price.toFixed(2)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toString();
  };

  if (!token) {
    console.log('❌ [PriceChartModal] No token provided');
    return null;
  }

  console.log('🚀 [PriceChartModal] Rendering modal for token:', token.symbol, token.contractAddress);

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-1 sm:p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          console.log('🚀 [PriceChartModal] Backdrop clicked, closing modal');
          onClose();
        }
      }}
    >
      <div 
        className="bg-gray-900 rounded-xl sm:rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-2 sm:p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            {/* Token Icon */}
            <div className="w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
              {token.jupiterData?.icon ? (
                <img 
                  src={token.jupiterData.icon} 
                  alt={token.symbol}
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                />
              ) : (
                <span className="text-white font-bold text-sm sm:text-lg">
                  {token.symbol?.charAt(0) || '?'}
                </span>
              )}
            </div>
            
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-xl font-bold text-white truncate">{token.symbol}</h2>
              <p className="text-gray-400 text-xs sm:text-sm truncate">{token.name}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-1 sm:space-x-4 flex-shrink-0">
            {/* Oracle Chart Button - Premium only for AI Analysis toggle */}
            <button
              onClick={() => {
                setShowTechnicalAnalysis(!showTechnicalAnalysis);
              }}
              disabled={!isAuthenticated || !isPremiumUser}
              className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors border text-xs sm:text-sm ${
                !isAuthenticated || !isPremiumUser
                  ? 'bg-gray-600 text-gray-400 border-gray-500 cursor-not-allowed opacity-60'
                  : showTechnicalAnalysis
                  ? 'bg-purple-600 text-white border-purple-500 hover:bg-purple-700'
                  : 'bg-gray-600 text-white border-purple-500 hover:bg-gray-500'
              }`}
            >
              <TrendingUp size={16} />
              <span>Oracle Chart</span>
              {showTechnicalAnalysis && isPremiumUser && (
                <span className="text-xs bg-purple-500 px-2 py-0.5 rounded-full">
                  AI Analysis
                </span>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Price Info */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0">
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">
                  {currentPrice ? formatPrice(currentPrice) : 'Loading...'}
                </div>
                <div className={`text-sm font-medium ${
                  priceChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                  {livePriceChange !== null && <span className="text-green-400 ml-1">📡</span>}
                </div>
              </div>
              
              <div className="text-xs sm:text-sm text-gray-400 space-y-1">
                <div>
                  Market Cap: {formatNumber(liveMarketCap || token.jupiterData?.mcap || token.marketCap || 0)}
                  {liveMarketCap && <span className="text-green-400 ml-1">📡</span>}
                </div>
                <div>
                  Volume ({timeframe}): {formatNumber(volume)}
                  {volume > 0 && <span className="text-green-400 ml-1">📡</span>}
                </div>
                {liveLiquidity && (
                  <div>
                    Liquidity: {formatNumber(liveLiquidity)}
                    <span className="text-green-400 ml-1">📡</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chart Container - Scrollable */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 hover:scrollbar-thumb-gray-500">
          {/* Technical Analysis Panel */}
          {showTechnicalAnalysis && isPremiumUser && (
            <div className="mb-4">
              <TechnicalAnalysisPanel
                contractAddress={token.contractAddress}
                chartData={chartData}
                timeframe={timeframe}
                isVisible={showTechnicalAnalysis}
              />
            </div>
          )}
          
                 <ErrorBoundary>
                   <SVGChart token={token} onClose={onClose} />
                 </ErrorBoundary>
          
          {/* Swap Table - COMPLETELY ISOLATED TO PREVENT EVENT BUBBLING */}
          <div 
            className="mt-6"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
          >
            <ErrorBoundary>
              <SwapTable 
                key={`swaptable-${token.contractAddress}`}
                token={token} 
                realTimeData={realTimeData} 
              />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceChartModal;
